// frontend/src/components/mobile/VisionMobileNavigation.jsx
// Project:      Vision Systems / Vision ANPR
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-09-23
// Updated:      2026-09-23 (React 19 default-value hotfix)
//
// Description:  Vision-facing adapter for MobileBottomNav. Existing application
//               flyout/modal functions are passed in from the shell, while real
//               RBAC permission names remain owned by the current auth layer.

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import MobileBottomNav from './MobileBottomNav';
import {
  buildVisionMobileQuickActions,
  runVisionMobileQuickAction,
  VISION_MOBILE_NAVIGATION_ITEMS,
} from './mobileNavigationConfig';

const denyAccess = () => false;
const EMPTY_PERMISSION_MAP = Object.freeze({});

export default function VisionMobileNavigation({
  activeTab = null,
  canAccess = denyAccess,
  navigationItems = VISION_MOBILE_NAVIGATION_ITEMS,
  onNavigate,
  openContactCreate = null,
  openCustomerCreate = null,
  openExpenseCreate = null,
  openJobCreate = null,
  openOpportunityCreate = null,
  openQuoteCreate = null,
  openTaskCreate = null,
  permissionByAction = EMPTY_PERMISSION_MAP,
}) {
  const quickActions = useMemo(
    () =>
      buildVisionMobileQuickActions({
        canAccess,
        handlers: {
          openContactCreate,
          openCustomerCreate,
          openExpenseCreate,
          openJobCreate,
          openOpportunityCreate,
          openQuoteCreate,
          openTaskCreate,
        },
        permissionByAction,
      }),
    [
      canAccess,
      openContactCreate,
      openCustomerCreate,
      openExpenseCreate,
      openJobCreate,
      openOpportunityCreate,
      openQuoteCreate,
      openTaskCreate,
      permissionByAction,
    ],
  );

  const handleAction = useCallback((actionId, action) => {
    runVisionMobileQuickAction(actionId, action);
  }, []);

  return (
    <MobileBottomNav
      activeTab={activeTab}
      navigationItems={navigationItems}
      onAction={handleAction}
      onNavigate={onNavigate}
      quickActions={quickActions}
    />
  );
}

VisionMobileNavigation.propTypes = {
  activeTab: PropTypes.string,
  canAccess: PropTypes.func,
  navigationItems: PropTypes.arrayOf(PropTypes.object),
  onNavigate: PropTypes.func.isRequired,
  openContactCreate: PropTypes.func,
  openCustomerCreate: PropTypes.func,
  openExpenseCreate: PropTypes.func,
  openJobCreate: PropTypes.func,
  openOpportunityCreate: PropTypes.func,
  openQuoteCreate: PropTypes.func,
  openTaskCreate: PropTypes.func,
  permissionByAction: PropTypes.objectOf(PropTypes.string),
};
